import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, CheckCircle, Clock, Phone, Video, ArrowRight } from 'lucide-react'
import { id } from '@instantdb/react'
import DatePicker from '../components/ui/DatePicker'
import Seo from '../components/Seo'
import { db } from '../lib/db'

const INTEREST_OPTIONS = [
  'The Strong Again Method™',
  'Nutrition Blueprint',
  'Physique & Competition Coaching',
 'DMV In-Person Training Add-On',
  'Not Sure Yet',
] as const
const FITNESS_LEVELS = ['Beginner', 'Intermediate', 'Advanced'] as const

const AGE_RANGES = [
  '18–24',
  '25–34',
  '35–44',
  '45–54',
  '55–64',
  '65+',
  'Prefer not to say',
] as const

const TIME_SLOTS = (() => {
  const slots: { value: string; label: string }[] = []
  for (let hour = 8; hour <= 18; hour++) {
    for (const minute of [0, 30]) {
      if (hour === 18 && minute === 30) break
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
      const period = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour % 12 === 0 ? 12 : hour % 12
      const label = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`
      slots.push({ value, label })
    }
  }
  return slots
})()

const tomorrowISO = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(0, 0, 0, 0)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const MANAGE_LINK_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

// Generated on the device at booking time, using the browser's secure
// random number generator, so the manage token is saved as part of the
// very same write that creates the booking (no separate server step
// that could race against the new record).
const generateManageToken = () => {
  const bytes = new Uint8Array(24)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

type FormState = {
  full_name: string
  email: string
  phone: string
  format: 'phone' | 'video' | ''
  preferred_date: string
  preferred_time: string
  interested_in: string[]
  goals: string
  fitness_level: string
  biggest_challenge: string
  injuries: string
  age_range: string
  location: string
  referral_source: string
  notes: string
}

const initialForm: FormState = {
  full_name: '',
  email: '',
  phone: '',
  format: '',
  preferred_date: '',
  preferred_time: '',
  interested_in: [],
  goals: '',
  fitness_level: '',
  biggest_challenge: '',
  injuries: '',
  age_range: '',
  location: '',
  referral_source: '',
  notes: '',
}

const BookConsultation: React.FC = () => {
  const [form, setForm] = useState<FormState>(() => {
    const params = new URLSearchParams(window.location.search)
    const src = params.get('src')
    return src === 'first-step'
      ? { ...initialForm, referral_source: 'The First Step reading' }
      : initialForm
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [cancelled, setCancelled] = useState(false)
  const [error, setError] = useState('')
  const [requestId, setRequestId] = useState<string | null>(null)
  const [manageToken, setManageToken] = useState<string | null>(null)
  const minDate = useMemo(() => tomorrowISO(), [])
  const update = (field: keyof FormState, value: string | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const toggleInterest = (option: string) => {
    setForm((prev) => {
      const exists = prev.interested_in.includes(option)
      return {
        ...prev,
        interested_in: exists
          ? prev.interested_in.filter((item) => item !== option)
          : [...prev.interested_in, option],
      }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.format) {
      setError('Please choose Phone or Video for your consultation.')
      return
    }

    if (!form.preferred_date || form.preferred_date < minDate) {
      setError('Please choose a date starting tomorrow or later.')
      return
    }

    if (form.interested_in.length === 0) {
      setError('Please select at least one area of interest.')
      return
    }

    setLoading(true)

    try {
      if (!process.env.REACT_APP_INSTANT_APP_ID) {
        setError(
          'Booking is almost ready — InstantDB is not configured yet. Please email hello@fitbycassie.com.'
        )
        return
      }

      const isUpdate = Boolean(requestId)
      const currentRequestId = requestId || id()

      if (!isUpdate) {
        const newManageToken = generateManageToken()
        const newManageTokenExpiresAt = Date.now() + MANAGE_LINK_TTL_MS

        await db.transact(
          db.tx.consultationRequests[currentRequestId].update({
            createdAt: Date.now(),
            fullName: form.full_name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            format: form.format,
            preferredDate: form.preferred_date,
            preferredTime: form.preferred_time,
            interestedIn: form.interested_in,
            goals: form.goals.trim(),
            status: 'new',
            manageToken: newManageToken,
            manageTokenExpiresAt: newManageTokenExpiresAt,
            ...(form.fitness_level
              ? { fitnessLevel: form.fitness_level }
              : {}),
            ...(form.biggest_challenge.trim()
              ? { biggestChallenge: form.biggest_challenge.trim() }
              : {}),
            ...(form.injuries.trim()
              ? { injuries: form.injuries.trim() }
              : {}),
            ...(form.age_range ? { ageRange: form.age_range } : {}),
            ...(form.location.trim()
              ? { location: form.location.trim() }
              : {}),
            ...(form.referral_source.trim()
              ? { referralSource: form.referral_source.trim() }
              : {}),
            ...(form.notes.trim()
              ? { notes: form.notes.trim() }
              : {}),
          })
        )

        setRequestId(currentRequestId)
        setManageToken(newManageToken)

        void fetch('/api/consultation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            requestId: currentRequestId,
            manageToken: newManageToken,
            isUpdate: false,
          }),
        }).catch((emailError) => {
          console.error('Consultation notification failed:', emailError)
        })
      } else {
        const response = await fetch('/api/consultation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            requestId: currentRequestId,
            manageToken,
            isUpdate: true,
          }),
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(
            `Consultation update failed (${response.status}): ${errorText}`
          )
        }
      }

      setSuccess(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error(err)
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong submitting your request. Please try again or email hello@fitbycassie.com.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!requestId) return

    const confirmed = window.confirm(
      'Are you sure you want to cancel this consultation request?'
    )
    if (!confirmed) return

    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/consultation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          requestId,
          manageToken,
          isCancel: true,
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(
          `Consultation cancellation failed (${response.status}): ${errorText}`
        )
      }

      setCancelled(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error(err)
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong cancelling your request. Please try again or email hello@fitbycassie.com.'
      )
    } finally {
      setLoading(false)
    }
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-cream">
        <Seo
          title="Consultation Request Cancelled"
          description="Your consultation request with Cassie has been cancelled."
        />
        <section className="section-padding">
          <div className="container-max max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-soft border border-gray-100 p-10 sm:p-14"
            >
              <h1 className="heading-section mb-4">Request cancelled</h1>
              <p className="lead mb-8">
                Your consultation request has been cancelled. If you change your mind, you&apos;re welcome to submit a new request anytime.
              </p>
              <Link to="/" className="btn-primary">
                Back to Home
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-cream">
        <Seo
          title="Consultation Request Received"
          description="Your complimentary 30-minute consultation request with Cassie has been received."
        />
        <section className="section-padding">
          <div className="container-max max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-soft border border-gray-100 p-10 sm:p-14"
            >
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center">
                <CheckCircle className="text-brand" size={32} />
              </div>
              <h1 className="heading-section mb-4">Request received</h1>
              <p className="lead mb-8">
                Cassie will review your intake and confirm your complimentary 30-minute consultation
                by email or text. Watch for a confirmation soon.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                We&apos;ve also emailed you a secure link so you can manage this request anytime — it&apos;s valid for 24 hours.
              </p>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm mb-8 text-left">
                  {error}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/" className="btn-primary">
                  Back to Home
                </Link>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  disabled={loading}
                  className="btn-secondary"
                >
                  Change Date or Time
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  className="btn-secondary !border-red-200 !text-red-600 hover:!bg-red-50"
                >
                  Cancel Request
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-cream">
      <Seo

  title="Book a Complimentary 30-Minute Consultation"
  description="Tell Cassie about your goals and request a complimentary 30-minute phone or video consultation for personalized coaching."
/>
      {/* Hero */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-brand/20 rounded-full blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-32 -left-20 w-[360px] h-[360px] bg-gold/10 rounded-full blur-3xl pointer-events-none" aria-hidden />
        <div className="container-max relative px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="eyebrow-gold justify-center mb-4">No pressure · No commitment</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 leading-tight">

              Complimentary 30-Minute <em className="italic text-gold-light">Consultation</em>
            </h1>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
Tell Cassie a little about yourself, your goals, and what kind of support you&apos;re looking for.
Choose a preferred time for your complimentary 30-minute phone or video consultation, and Cassie will confirm the details.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <span className="inline-flex items-center gap-2">
                <Clock size={16} className="text-gold-light" /> 30 minutes
              </span>
              <span className="inline-flex items-center gap-2">
                <Phone size={16} className="text-gold-light" /> Phone
              </span>
              <span className="inline-flex items-center gap-2">
                <Video size={16} className="text-gold-light" /> Video
              </span>
              <span className="inline-flex items-center gap-2">
                <Calendar size={16} className="text-gold-light" /> Pick your time
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding">
        <div className="container-max max-w-3xl">
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-3xl shadow-soft border border-gray-100 p-5 sm:p-8 lg:p-10 space-y-10 -mt-8 sm:-mt-12 relative z-10"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            {/* Contact basics */}
            <div>
              <h2 className="font-display text-2xl font-semibold text-ink mb-1">About you</h2>
              <p className="text-sm text-gray-500 mb-6">So Cassie knows who she&apos;s talking to.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label htmlFor="full_name" className="form-label">Full Name *</label>
                  <input
                    id="full_name"
                    required
                    value={form.full_name}
                    onChange={(e) => update('full_name', e.target.value)}
                    className="form-input"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    className="form-input"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="form-label">Phone *</label>
                  <input
                    id="phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    className="form-input"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label htmlFor="age_range" className="form-label">Age Range</label>
                  <select
                    id="age_range"
                    value={form.age_range}
                    onChange={(e) => update('age_range', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select age range</option>
                    {AGE_RANGES.map((range) => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="location" className="form-label">City / Location</label>
                  <input
                    id="location"
                    value={form.location}
                    onChange={(e) => update('location', e.target.value)}
                    className="form-input"
                    placeholder="e.g. Arlington, VA"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Optional in-person training may be available for select DMV coaching clients, subject to location and availability. Online coaching is available from anywhere.                  </p>
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div>
              <h2 className="font-display text-2xl font-semibold text-ink mb-1">Schedule your call</h2>
              <p className="text-sm text-gray-500 mb-6">
           Consultations are 30 minutes. Times are Eastern Time (ET). Cassie will confirm your consultation details.
              </p>

              <div className="mb-6">
                <span className="form-label">Preferred Format *</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  {[
                    { value: 'phone' as const, label: 'Phone Call', icon: Phone, desc: 'Cassie will call your number' },
                    { value: 'video' as const, label: 'Video Call', icon: Video, desc: 'Zoom / FaceTime style' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => update('format', option.value)}
                      className={`text-left rounded-2xl border px-5 py-4 transition-all ${
                        form.format === option.value
                          ? 'border-brand bg-brand-soft shadow-soft'
                          : 'border-gray-200 hover:border-brand/40'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <option.icon size={18} className={form.format === option.value ? 'text-brand' : 'text-gray-500'} />
                        <span className="font-semibold text-ink">{option.label}</span>
                      </div>
                      <p className="text-sm text-gray-500 pl-8">{option.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="form-label">Preferred Date *</label>
                  <DatePicker
                    value={form.preferred_date}
                    onChange={(date) => update('preferred_date', date)}
                    placeholder="Select a date"
                    className="form-input !border-gray-200 !rounded-xl !py-3.5"
                    required
                    min={minDate}
                  />
                  <p className="text-xs text-gray-500 mt-1.5">Earliest selectable date is tomorrow.</p>
                </div>
                <div>
                  <label htmlFor="preferred_time" className="form-label"> Preferred Time (ET) *</label>
                  <select
                    id="preferred_time"
                    required
                    value={form.preferred_time}
                    onChange={(e) => update('preferred_time', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select a 30-min slot</option>
                    {TIME_SLOTS.map((slot) => (
                      <option key={slot.value} value={slot.value}>{slot.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Goals */}
            <div>
              <h2 className="font-display text-2xl font-semibold text-ink mb-1">Your goals</h2>
              <p className="text-sm text-gray-500 mb-6">Help Cassie understand your goals before your consultation.
              </p>

              <div className="mb-6">
                <span className="form-label">Interested In *</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {INTEREST_OPTIONS.map((option) => {
                    const active = form.interested_in.includes(option)
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => toggleInterest(option)}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                          active
                            ? 'bg-ink text-white border-ink'
                            : 'bg-white text-ink border-gray-200 hover:border-brand'
                        }`}
                      >
                        {option}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label htmlFor="goals" className="form-label">What are your main goals? *</label>
                  <textarea
                    id="goals"
                    required
                    rows={4}
                    value={form.goals}
                    onChange={(e) => update('goals', e.target.value)}
                    className="form-input resize-none"
                    placeholder="e.g. lose 20 lbs, build strength, feel confident again, prep for a show..."
                  />
                </div>

                <div>
                  <label htmlFor="fitness_level" className="form-label">Current Fitness Level</label>
                  <select
                    id="fitness_level"
                    value={form.fitness_level}
                    onChange={(e) => update('fitness_level', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select level</option>
                    {FITNESS_LEVELS.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="biggest_challenge" className="form-label">Biggest challenge right now</label>
                  <textarea
                    id="biggest_challenge"
                    rows={3}
                    value={form.biggest_challenge}
                    onChange={(e) => update('biggest_challenge', e.target.value)}
                    className="form-input resize-none"
                    placeholder="Consistency, nutrition, time, motivation, starting over..."
                  />
                </div>

                <div>
                  <label htmlFor="injuries" className="form-label">Injuries or limitations (optional)</label>
                  <textarea
                    id="injuries"
                    rows={3}
                    value={form.injuries}
                    onChange={(e) => update('injuries', e.target.value)}
                    className="form-input resize-none"
                    placeholder="Anything Cassie should know before recommending a plan"
                  />
                </div>
              </div>
            </div>

            {/* Extra */}
            <div>
              <h2 className="font-display text-2xl font-semibold text-ink mb-1">Anything else?</h2>
              <p className="text-sm text-gray-500 mb-6">Optional, but helpful.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label htmlFor="referral_source" className="form-label">How did you hear about Cassie?</label>
                  <input
                    id="referral_source"
                    value={form.referral_source}
                    onChange={(e) => update('referral_source', e.target.value)}
                    className="form-input"
                    placeholder="Instagram, friend, Google, etc."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="notes" className="form-label">Additional notes</label>
                  <textarea
                    id="notes"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => update('notes', e.target.value)}
                    className="form-input resize-none"
                    placeholder="Questions, schedule constraints, preferred contact method..."
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary !py-4 text-lg"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>

{requestId
  ? 'Update My Consultation Request'
  : 'Request My Complimentary Consultation'}
                    <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </button>
              <p className="text-center text-sm text-gray-500 mt-4">
                Looking for something else?{' '}
                <Link to="/contact" className="text-brand font-semibold hover:text-brand-dark">
                  Send a general message
                </Link>
              </p>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  )
}

export default BookConsultation
